"use client";

import React, { useCallback } from "react";
import { Button } from "@mantine/core";

// Types
import type { DetailActionProps, BaseEntity } from "../types/detailPage";

// ============================================================================
// CONTRACT GENERATION ACTION COMPONENT
// ============================================================================

interface ContractGenerationActionProps<T extends BaseEntity> extends DetailActionProps<T> {
  /** ID field name */
  idField?: string;

  /** Contract URL template */
  contractUrlTemplate?: string;
}

/**
 * Reusable Contract Generation Action Component
 * Can be used for any entity that can generate contracts (contacts, companies, etc.)
 */
export function ContractGenerationAction<T extends BaseEntity>({
  action,
  item,
  context,
  loading = false,
  idField = "id",
  contractUrlTemplate = "/contracts?client={id}",
}: ContractGenerationActionProps<T>) {
  // Handle contract generation
  const handleGenerateContract = useCallback(() => {
    const rawId = (item as Record<string, unknown>)[idField];
    const entityId = typeof rawId === "string" ? rawId : String(rawId ?? "");
    const contractUrl = contractUrlTemplate.replace("{id}", entityId);
    context.onNavigate(contractUrl);
  }, [item, idField, contractUrlTemplate, context]);

  return (
    <Button variant="outline" size="sm" onClick={handleGenerateContract} disabled={loading}>
      Generate Contract
    </Button>
  );
}

// Export with generic type
export { ContractGenerationAction as ContractGenerationActionComponent };
